import type {
  SubmitCollectorRequest,
  SubmitCollectorResponse,
} from '../types/data-collection-form.types';
import { API_TAGS } from '@/shared/api/api-tags';
import { baseApi } from '@/shared/api/base-api';
import { buildBranchListQueryString } from '@/shared/api/normalize-pro20-page';
import { normalizePro20Response } from '@/shared/api/pro20-transform';
import type { PaginatedResponse } from '@/shared/types/api.types';

import type {
  CreateRedirectFormPayload,
  DataCollectionForm,
  DuplicateRedirectFormParams,
  FormCollector,
  FormCollectorDetailParams,
  FormCollectorListParams,
  MultimediaEntity,
  RedirectFormImageRemoveParams,
  RedirectFormImageUploadParams,
  UpdateRedirectFormPayload,
  UpdateRedirectFormStatusParams,
} from '../types/data-collection-form.types';
import { normalizeFormCollectorsPageResponse } from '../utils/data-collection-form-formatters';
import { attachFormDescription } from '../utils/form-description';

export const dataCollectionFormsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    submitCollectorAnswers: builder.mutation<
      SubmitCollectorResponse,
      SubmitCollectorRequest
    >({
      query: ({ formCode, body }) => ({
        url: `/form/public/${encodeURIComponent(formCode)}/submissions`,
        method: 'POST',
        body,
      }),
      transformResponse: (response: unknown) =>
        normalizePro20Response<SubmitCollectorResponse>(response as never),
    }),
    getFormCollectorsByBranch: builder.query<
      PaginatedResponse<DataCollectionForm>,
      FormCollectorListParams
    >({
      query: ({ branchCode, page, size, sortBy, sortDirection, search }) => ({
        url: `/form/retrieveformcollectorbybranchcode/${branchCode}${buildBranchListQueryString(
          {
            page,
            size,
            sortBy,
            sortDirection,
            search,
          },
        )}`,
        method: 'GET',
      }),
      transformResponse: (response: unknown, _meta, arg) =>
        normalizeFormCollectorsPageResponse(normalizePro20Response(response), {
          page: arg.page ?? 0,
          size: arg.size ?? 10,
        }),
      providesTags: (_result, _error, { branchCode }) => [
        { type: API_TAGS.DataCollectionForms, id: branchCode },
      ],
    }),
    getFormCollectorByCode: builder.query<
      FormCollector,
      FormCollectorDetailParams
    >({
      query: ({ formCode }) => ({
        url: `/form/retrieveformcollectorbybranchcodeandformcode/${formCode}`,
        method: 'GET',
      }),
      transformResponse: (response: unknown) =>
        attachFormDescription(
          normalizePro20Response<FormCollector>(response as never),
        ),
      providesTags: (result) =>
        result
          ? [{ type: API_TAGS.DataCollectionForms, id: result.formCode }]
          : [],
    }),
    createRedirectForm: builder.mutation<
      FormCollector,
      CreateRedirectFormPayload
    >({
      query: (body) => ({
        url: '/form/create/redirectform',
        method: 'POST',
        body,
      }),
      transformResponse: (response: unknown) =>
        attachFormDescription(
          normalizePro20Response<FormCollector>(response as never),
        ),
      invalidatesTags: (_result, _error, { branchCode }) => [
        { type: API_TAGS.DataCollectionForms, id: branchCode },
      ],
    }),
    updateRedirectForm: builder.mutation<
      FormCollector,
      UpdateRedirectFormPayload
    >({
      query: (body) => ({
        url: '/form/update/redirectform',
        method: 'PUT',
        body,
      }),
      transformResponse: (response: unknown) =>
        attachFormDescription(
          normalizePro20Response<FormCollector>(response as never),
        ),
      invalidatesTags: (result, _error, arg) => [
        { type: API_TAGS.DataCollectionForms, id: arg.branchCode },
        { type: API_TAGS.DataCollectionForms, id: arg.formCode },
        ...(result
          ? [{ type: API_TAGS.DataCollectionForms, id: result.formCode }]
          : []),
      ],
    }),
    uploadRedirectFormLogo: builder.mutation<
      MultimediaEntity,
      RedirectFormImageUploadParams
    >({
      query: ({ formCode, formData }) => ({
        url: `/form/upload/redirectformlogophoto/${formCode}`,
        method: 'POST',
        body: formData,
      }),
      transformResponse: (response: unknown) =>
        normalizePro20Response<MultimediaEntity>(response as never),
    }),
    uploadRedirectFormBackground: builder.mutation<
      MultimediaEntity,
      RedirectFormImageUploadParams
    >({
      query: ({ formCode, formData }) => ({
        url: `/form/upload/redirectformbackgroundphoto/${formCode}`,
        method: 'POST',
        body: formData,
      }),
      transformResponse: (response: unknown) =>
        normalizePro20Response<MultimediaEntity>(response as never),
    }),
    removeRedirectFormLogo: builder.mutation<
      unknown,
      RedirectFormImageRemoveParams
    >({
      query: ({ formCode }) => ({
        url: `/form/remove/redirectformlogophoto/${formCode}`,
        method: 'DELETE',
      }),
    }),
    removeRedirectFormBackground: builder.mutation<
      unknown,
      RedirectFormImageRemoveParams
    >({
      query: ({ formCode }) => ({
        url: `/form/remove/redirectformbackgroundphoto/${formCode}`,
        method: 'DELETE',
      }),
    }),
    updateRedirectFormStatus: builder.mutation<
      unknown,
      UpdateRedirectFormStatusParams
    >({
      query: ({ formCode, status }) => ({
        url: `/form/update/redirectform/status/${formCode}/${status}`,
        method: 'PUT',
      }),
      invalidatesTags: (_result, _error, { branchCode }) =>
        branchCode
          ? [{ type: API_TAGS.DataCollectionForms, id: branchCode }]
          : [],
    }),
    duplicateRedirectForm: builder.mutation<
      FormCollector,
      DuplicateRedirectFormParams
    >({
      query: ({ formCode }) => ({
        url: `/form/duplicate/redirectform/${formCode}`,
        method: 'PUT',
      }),
      transformResponse: (response: unknown) =>
        attachFormDescription(
          normalizePro20Response<FormCollector>(response as never),
        ),
      invalidatesTags: (_result, _error, { branchCode }) =>
        branchCode
          ? [{ type: API_TAGS.DataCollectionForms, id: branchCode }]
          : [],
    }),
  }),
});

export const {
  useSubmitCollectorAnswersMutation,
  useGetFormCollectorsByBranchQuery,
  useGetFormCollectorByCodeQuery,
  useCreateRedirectFormMutation,
  useUpdateRedirectFormMutation,
  useUploadRedirectFormLogoMutation,
  useUploadRedirectFormBackgroundMutation,
  useRemoveRedirectFormLogoMutation,
  useRemoveRedirectFormBackgroundMutation,
  useUpdateRedirectFormStatusMutation,
  useDuplicateRedirectFormMutation,
} = dataCollectionFormsApi;
